import React from "react";
import { Download, FileText, FolderInput, Layers, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";

type GoogleDriveImportBarProps = {
  selectedCount: number;
  staleCount: number;
  rootFileCount: number;
  folderCount: number;
  isImporting: boolean;
  isScanning: boolean;
  progress?: { current: string; completed: number; total: number } | null;
  onImport: () => void;
  onImportRootFiles: () => void;
  onImportFolders: () => void;
  onImportAll: () => void;
  onUpdateStale: () => void;
  onRefresh: () => void;
  onClearSelection: () => void;
};

export const GoogleDriveImportBar = React.memo(function GoogleDriveImportBar({
  selectedCount,
  staleCount,
  rootFileCount,
  folderCount,
  isImporting,
  isScanning,
  progress,
  onImport,
  onImportRootFiles,
  onImportFolders,
  onImportAll,
  onUpdateStale,
  onRefresh,
  onClearSelection,
}: GoogleDriveImportBarProps) {
  const busy = isImporting || isScanning;

  return (
    <div className="space-y-2 rounded-md border bg-background p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          onClick={onImport}
          disabled={selectedCount === 0 || busy}
        >
          {isImporting ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Download className="mr-2 h-4 w-4" />
          )}
          Import {selectedCount > 0 ? `(${selectedCount})` : ""}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={onImportRootFiles}
          disabled={rootFileCount === 0 || busy}
          title="Import files in this folder only (not inside subfolders)"
        >
          {isScanning ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <FileText className="mr-2 h-4 w-4" />
          )}
          Import files here{rootFileCount > 0 ? ` (${rootFileCount})` : ""}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={onImportFolders}
          disabled={folderCount === 0 || busy}
          title="Import all files inside folders shown here (including nested folders)"
        >
          {isScanning ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <FolderInput className="mr-2 h-4 w-4" />
          )}
          Import folders{folderCount > 0 ? ` (${folderCount})` : ""}
        </Button>
        <Button
          size="sm"
          variant="secondary"
          onClick={onImportAll}
          disabled={(rootFileCount === 0 && folderCount === 0) || busy}
          title="Import files here plus all files inside subfolders"
        >
          {isScanning ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Layers className="mr-2 h-4 w-4" />
          )}
          Import all
        </Button>
        {staleCount > 0 && (
          <Button
            size="sm"
            variant="secondary"
            onClick={onUpdateStale}
            disabled={busy}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Update &amp; re-embed ({staleCount})
          </Button>
        )}
        <Button
          size="sm"
          variant="outline"
          onClick={onRefresh}
          disabled={busy}
        >
          <RefreshCw className="mr-2 h-4 w-4" />
          Check for updates
        </Button>
        {selectedCount > 0 && (
          <Button
            size="sm"
            variant="ghost"
            onClick={onClearSelection}
            disabled={busy}
          >
            Clear selection
          </Button>
        )}
      </div>
      {progress && (progress.total > 0 || isScanning) && (
        <div className="space-y-1">
          <div className="flex justify-between text-xs text-muted-foreground">
            <span className="truncate">{progress.current}</span>
            {progress.total > 0 && (
              <span>
                {progress.completed} / {progress.total}
              </span>
            )}
          </div>
          {progress.total > 0 ? (
            <Progress
              value={(progress.completed / progress.total) * 100}
              className="h-1.5"
            />
          ) : (
            <Progress value={undefined} className="h-1.5" />
          )}
        </div>
      )}
    </div>
  );
});
